import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/Input";
import { TextArea } from "@/components/Textarea";
import { useAddEditSectionMutation } from "@/redux/services/menu/menu.api";
import toast from "react-hot-toast";
import { Section, SectionFormData } from "@/redux/services/menu/menu.type";
import { useUploadMedia } from "@/lib/useUploadMedia";
import { toastApiError } from "@/lib/apiErrors";

interface SectionFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  section?: Section;
  menuId: string;
  numberOfMenuSections?: number;
  onSuccess?: () => void;
}

const SectionFormModal: React.FC<SectionFormModalProps> = ({
  isOpen,
  onClose,
  section,
  menuId,
  numberOfMenuSections,
  onSuccess,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sortOrder, setSortOrder] = useState<number>(1);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePath, setImagePath] = useState<string>("");

  const [addEditSection, { isLoading }] = useAddEditSectionMutation();
  const { uploadMedia } = useUploadMedia();

  const isEditing = !!section;

  useEffect(() => {
    if (section) {
      setTitle(section.title);
      setDescription(section.description || "");
      setSortOrder(section.sortOrder || 1);
      if (section.image?.url) {
        setImagePreview(section.image.url);
        setImagePath(`/api/media_objects/${section.image.id}`);
      } else {
        setImagePreview("");
        setImagePath("");
      }
    } else {
      setTitle("");
      setDescription("");
      setSortOrder((numberOfMenuSections ?? 0) + 1);
      setImagePreview("");
      setImagePath("");
      setImageFile(null);
    }
  }, [section, isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Le titre de la section est requis");
      return;
    }

    try {
      let finalImagePath = imagePath;

      if (imageFile) {
        const uploadedMedia = await uploadMedia(imageFile);
        if (uploadedMedia) {
          finalImagePath = `/api/media_objects/${uploadedMedia.id}`;
        }
      }

      const sectionData: SectionFormData = {
        title,
        description,
        sortOrder,
        ...(finalImagePath && { image: finalImagePath }),
      };

      if (!isEditing) {
        sectionData.menu = `/api/menus/${menuId}`;
      }

      await addEditSection({
        id: section?.id,
        data: sectionData,
      }).unwrap();

      toast.success(
        `Section ${isEditing ? "mise à jour" : "ajoutée"} avec succès`
      );

      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    } catch (error: any) {
      toastApiError(error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier la section" : "Ajouter une section"}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              {imagePreview && (
                <div className="mb-2">
                  <img
                    src={imagePreview}
                    alt="Section preview"
                    className="w-10 h-10 object-cover rounded"
                  />
                </div>
              )}
              <label className="text-gray-700" htmlFor="image">
                Image
              </label>
              <input
                type="file"
                id="image"
                className="block py-4 px-3 w-full border bg-white rounded-sm mt-1"
                onChange={handleImageChange}
                accept="image/*"
              />
            </div>

            <div>
              <Input
                label="Titre"
                type="text"
                id="title"
                placeholder="Titre de la section"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div>
              <TextArea
                label="Description"
                placeholder="Description de la section"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div>
              <Input
                label="Position"
                type="number"
                id="sortOrder"
                placeholder="Position"
                value={sortOrder}
                onChange={(e) => setSortOrder(parseInt(e.target.value))}
                min={1}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              isLoading={isLoading}
            >
              {isEditing ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SectionFormModal;
